import React, { isValidElement, useEffect, forwardRef } from "react"
import { Dialog, Portal } from "@ark-ui/react"
import cx from "classnames"

import ModalHeader from "./ModalHeader"
import ModalFooter, { ModalFooterPropsType } from "./ModalFooter"

export type ModalPropsType = {
  /**
   * mandatory for A11y
   */
  ["aria-label"]: string
  /**
   * Function called to close the modal, providing it make the modal closeable
   */
  close?: () => void
  /**
   * React.ref of the element to focus first when the modal opens
   */
  initialFocusRef?: React.RefObject<HTMLElement>
  /**
   * State to show or hide the modal/dialog
   */
  isOpen: boolean
  /**
   * Custom css classes to add to the modal
   */
  className?: string
  /**
   * Action to fire if the user try to dismiss when the modal is not closeable
   */
  onDismissAttempt?: () => void
  /**
   * Listener called when the modal has been fully hidden
   */
  onHidden?: () => void
  /**
   * Listener called when the modal begins to show
   */
  onShow?: () => void
  /**
   * Modal title
   */
  title?: React.ReactNode
  /**
   * Disable/Enable body paddings (enabled by default)
   */
  bodySpacing?: boolean
  /**
   * Disable/Enable hidding overflowing absolute elements (enabled by default)
   */
  overflowHidden?: boolean
  /**
   * Disable enter/leave animation (animation enabled by default)
   */
  skipAnimation?: {
    enter: boolean
    leave: boolean
  }
  /**
   * Disable modal max-width limit
   */
  fullWidth?: boolean
  /**
   * Always take the full height of the screen
   */
  fullHeight?: boolean
  /**
   * Remove the modal from the DOM on hidden (enabled by default)
   */
  unmountOnHidden?: boolean
  /**
   * Modal body content
   */
  children: React.ReactNode
}

// Only for the API, render nothing, we use ModalFooterInternal internally
export const ModalFooterAPI = (_props: ModalFooterPropsType) => null

export const isModalFooterAPIComponent = (
  component: React.ReactNode
): component is React.ReactElement<ModalFooterPropsType> =>
  React.isValidElement(component) && component.type === ModalFooterAPI

const Modal = forwardRef<HTMLDivElement, ModalPropsType>(
  (
    {
      isOpen,
      close,
      className,
      initialFocusRef,
      ["aria-label"]: ariaLabel,
      title,
      children,
      overflowHidden = true,
      bodySpacing = true,
      fullWidth,
      fullHeight,
      onHidden,
      onShow,
      skipAnimation,
      onDismissAttempt,
    }: ModalPropsType,
    ref
  ) => {
    useEffect(() => {
      isOpen ? onShow?.() : close?.()
    }, [onShow, isOpen, close])

    const modalFooter = React.Children.toArray(children).find((c) =>
      isModalFooterAPIComponent(c)
    )
    const hasFooter = isValidElement(modalFooter)

    return (
      <Dialog.Root
        aria-label={ariaLabel}
        open={isOpen}
        onExitComplete={onHidden}
        initialFocusEl={initialFocusRef && (() => initialFocusRef.current)}
        onPointerDownOutside={close ? undefined : onDismissAttempt}
        lazyMount
        // For some reason, onHidden can sometimes not be called if we use unmountOnExit
        unmountOnExit={!onHidden}
      >
        <Portal>
          <Dialog.Backdrop
            className={cx("cobalt-modal__overlay", {
              "cobalt-modal--skipAnimation__enter": skipAnimation?.enter,
              "cobalt-modal--skipAnimation__leave": skipAnimation?.leave,
            })}
          >
            <Dialog.Positioner
              className={cx("cobalt-modal", {
                "cobalt-modal--overflowHidden": overflowHidden,
                "cobalt-modal--fullHeight": fullHeight,
                "cobalt-modal--fullWidth": fullWidth,
              })}
              ref={ref}
            >
              <Dialog.Content
                className={cx("cobalt-modal__content", className, {
                  "cobalt-modal--skipAnimation__enter": skipAnimation?.enter,
                  "cobalt-modal--skipAnimation__leave": skipAnimation?.leave,
                })}
              >
                <ModalHeader title={title} close={close} />
                <Dialog.Description className="cobalt-modal-body__wrapper">
                  <div
                    className={cx("cobalt-modal-body", {
                      "cobalt-modal-body--bodySpacing": bodySpacing,
                      "cobalt-modal-body--hasFooter": hasFooter,
                    })}
                  >
                    {children}
                  </div>
                  {hasFooter && <ModalFooter {...modalFooter.props} />}
                </Dialog.Description>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>
        </Portal>
      </Dialog.Root>
    )
  }
)

Modal.displayName = "Modal"

export default Object.assign(Modal, { Footer: ModalFooterAPI })

export { default as MultiStepModal, type ModalStepType } from "./MultiStepModal"
